<template>
  <Transition name="modal">
    <div v-if="isOpen && project" class="opm-backdrop" @click.self="$emit('close')">
      <div
        class="opm-content"
        role="dialog"
        aria-modal="true"
        :aria-label="`${project.name} details`"
      >
        <!-- Hero Image (optional) -->
        <div v-if="project.image" class="opm-hero">
          <img
            :src="project.image"
            :alt="`${project.name} preview`"
            class="opm-hero-img"
            loading="lazy"
          />
          <div class="opm-hero-overlay" aria-hidden="true"></div>
        </div>

        <!-- Header -->
        <div class="opm-header">
          <div class="opm-identity">
            <div class="opm-logo">
              <img
                v-if="project.logo"
                :src="project.logo"
                :alt="project.name"
                class="opm-logo-img"
              />
              <span v-else class="opm-logo-fallback">{{ project.name?.[0] }}</span>
            </div>
            <div class="opm-title-block">
              <h3 class="opm-name">{{ project.name }}</h3>
              <span v-if="project.duration" class="opm-duration">{{ project.duration }}</span>
            </div>
          </div>
          <button class="opm-close" @click="$emit('close')" aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M12 4L4 12M4 4l8 8"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </div>

        <!-- Body -->
        <div class="opm-body">
          <span class="opm-role-badge">{{ project.role }}</span>
          <p class="opm-description">{{ project.description }}</p>

          <!-- Slot for extended content -->
          <slot></slot>
        </div>

        <!-- Footer -->
        <div class="opm-footer">
          <div class="opm-tech">
            <span v-for="tag in visibleTags" :key="tag" class="opm-tech-chip">{{ tag }}</span>
            <button
              v-if="hiddenTags.length"
              ref="moreBtnRef"
              class="opm-tech-more"
              @click.stop="showTagsPopup = !showTagsPopup"
            >
              +{{ hiddenTags.length }}
              <div v-if="showTagsPopup" class="opm-tech-popup">
                <span v-for="tag in hiddenTags" :key="tag" class="opm-tech-chip">{{ tag }}</span>
              </div>
            </button>
          </div>
          <a
            v-if="project.url"
            :href="project.url"
            target="_blank"
            rel="noopener noreferrer"
            class="opm-visit-btn"
          >
            {{ $t('page.works.ui.visitProject') }} <span aria-hidden="true">↗</span>
          </a>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, computed, watch, onUnmounted } from 'vue'

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
  project: {
    type: Object,
    default: () => ({}),
  },
})

const emit = defineEmits(['close'])

// ============================================================================
// Tech tags — show 5, popup for the rest
// ============================================================================

const TAG_LIMIT = 5
const showTagsPopup = ref(false)
const moreBtnRef = ref(null)

const visibleTags = computed(() => props.project.tech?.slice(0, TAG_LIMIT) ?? [])
const hiddenTags = computed(() => props.project.tech?.slice(TAG_LIMIT) ?? [])

const closePopup = (e) => {
  if (moreBtnRef.value && !moreBtnRef.value.contains(e.target)) {
    showTagsPopup.value = false
  }
}

watch(showTagsPopup, (open) => {
  if (open) document.addEventListener('click', closePopup)
  else document.removeEventListener('click', closePopup)
})

watch(
  () => props.isOpen,
  () => {
    showTagsPopup.value = false
  }
)

// ============================================================================
// Scroll lock
// ============================================================================

const lockScroll = () => {
  document.body.style.overflow = 'hidden'
  document.documentElement.style.overflow = 'hidden'
  document.documentElement.classList.add('lenis-stopped')
  window.lenis?.stop()
}

const unlockScroll = () => {
  document.body.style.overflow = ''
  document.documentElement.style.overflow = ''
  document.documentElement.classList.remove('lenis-stopped')
  window.lenis?.start()
}

watch(
  () => props.isOpen,
  (open) => {
    if (open) {
      lockScroll()
    } else {
      unlockScroll()
    }
  }
)

onUnmounted(() => {
  unlockScroll()
})

// ============================================================================
// Escape key to close
// ============================================================================

const handleKeydown = (e) => {
  if (e.key === 'Escape' && props.isOpen) {
    emit('close')
  }
}

watch(
  () => props.isOpen,
  (open) => {
    if (open) {
      document.addEventListener('keydown', handleKeydown)
    } else {
      document.removeEventListener('keydown', handleKeydown)
    }
  }
)

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
/* ==========================================================================
   BACKDROP
   ========================================================================== */

.opm-backdrop {
  @apply fixed inset-0 flex items-center justify-center;
  z-index: 1000;
  background-color: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(8px);
}

/* ==========================================================================
   CONTENT
   ========================================================================== */

.opm-content {
  @apply w-full mx-4 overflow-hidden;
  max-width: 600px;
  max-height: 85vh;
  border-radius: 1.5rem;
  background: var(--color-deep-black);
  border: 1px solid var(--color-border);
  box-shadow:
    0 32px 80px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(139, 92, 246, 0.1);
  display: flex;
  flex-direction: column;
}

/* ==========================================================================
   HERO IMAGE
   ========================================================================== */

.opm-hero {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-top-left-radius: 1.5rem;
  border-top-right-radius: 1.5rem;
  flex-shrink: 0;
}

.opm-hero-img {
  @apply w-full h-full object-cover;
}

.opm-hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, var(--color-deep-black) 0%, transparent 40%);
  pointer-events: none;
}

/* ==========================================================================
   HEADER
   ========================================================================== */

.opm-header {
  @apply flex items-center justify-between;
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid var(--color-border);
  flex-shrink: 0;
}

.opm-identity {
  @apply flex items-center gap-3.5;
  min-width: 0;
}

.opm-logo {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.opm-logo-img {
  @apply w-full h-full object-cover;
}

.opm-logo-fallback {
  @apply text-lg font-bold uppercase;
  color: var(--color-electric-purple);
}

.opm-title-block {
  @apply flex flex-col gap-0.5;
  min-width: 0;
}

.opm-name {
  @apply text-lg font-bold truncate;
  color: var(--color-text-primary);
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.opm-duration {
  @apply text-xs;
  color: var(--color-text-muted);
  font-family: monospace;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.opm-close {
  @apply flex items-center justify-center flex-shrink-0;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid var(--color-border);
  color: var(--color-text-muted);
  background: transparent;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    color 0.2s ease,
    background-color 0.2s ease,
    transform 0.15s ease;
}

.opm-close:hover {
  border-color: var(--color-electric-purple);
  color: var(--color-text-primary);
  background: rgba(139, 92, 246, 0.1);
  transform: scale(1.05);
}

.opm-close:active {
  transform: scale(0.95);
}

/* ==========================================================================
   BODY
   ========================================================================== */

.opm-body {
  @apply flex flex-col gap-4;
  padding: 1.25rem 1.5rem;
  overflow-y: auto;
  flex: 1 1 auto;
  scrollbar-width: thin;
  scrollbar-color: var(--color-border) transparent;
}

.opm-body::-webkit-scrollbar {
  width: 4px;
}

.opm-body::-webkit-scrollbar-track {
  background: transparent;
}

.opm-body::-webkit-scrollbar-thumb {
  background: var(--color-border);
  border-radius: 2px;
}

.opm-role-badge {
  @apply inline-flex self-start text-xs font-semibold uppercase tracking-wider;
  padding: 0.3rem 0.75rem;
  border-radius: 100px;
  border: 1px solid var(--color-electric-purple);
  color: var(--color-electric-purple);
  font-family: monospace;
  letter-spacing: 0.06em;
}

.opm-description {
  @apply text-sm leading-relaxed;
  color: var(--color-text-secondary);
}

/* ==========================================================================
   FOOTER
   ========================================================================== */

.opm-footer {
  @apply flex items-center justify-between gap-3;
  padding: 1rem 1.5rem 1.5rem;
  border-top: 1px solid var(--color-border);
  flex-shrink: 0;
}

.opm-tech {
  @apply flex flex-wrap gap-1.5;
  flex: 1 1 auto;
}

.opm-tech-chip {
  @apply text-xs;
  padding: 0.25rem 0.625rem;
  border-radius: 6px;
  background: var(--color-surface-elevated);
  border: 1px solid var(--color-border);
  color: var(--color-text-muted);
  font-family: monospace;
  transition:
    border-color 0.2s ease,
    color 0.2s ease;
}

.opm-tech-chip:hover {
  border-color: var(--color-electric-purple);
  color: var(--color-text-primary);
}

.opm-tech-more {
  position: relative;
  @apply text-xs;
  padding: 0.25rem 0.625rem;
  border-radius: 6px;
  background: var(--color-surface-elevated);
  border: 1px solid var(--color-electric-purple);
  color: var(--color-electric-purple);
  font-family: monospace;
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    color 0.2s ease;
}

.opm-tech-more:hover {
  background: rgba(139, 92, 246, 0.12);
}

.opm-tech-popup {
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  padding: 0.625rem;
  background: var(--color-surface-elevated);
  border: 1px solid var(--color-border);
  border-radius: 0.625rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  white-space: nowrap;
  z-index: 10;
  min-width: max-content;
}

.opm-visit-btn {
  @apply flex-shrink-0 flex items-center gap-1.5 text-sm font-semibold;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: 1px solid var(--color-electric-purple);
  color: var(--color-electric-purple);
  background: transparent;
  text-decoration: none;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    transform 0.2s ease;
}

.opm-visit-btn:hover {
  background: rgba(139, 92, 246, 0.12);
  transform: translateY(-1px);
}

.opm-visit-btn:active {
  transform: translateY(0);
}

/* ==========================================================================
   TRANSITIONS
   ========================================================================== */

/* Backdrop */
.modal-enter-active {
  transition: opacity 0.35s cubic-bezier(0.16, 1, 0.3, 1);
}
.modal-leave-active {
  transition: opacity 0.2s ease-in;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

/* Content — enter */
.modal-enter-active .opm-content {
  animation: opm-in 0.45s cubic-bezier(0.16, 1, 0.3, 1) 0.05s both;
}

/* Content — exit */
.modal-leave-active .opm-content {
  animation: opm-out 0.18s cubic-bezier(0.4, 0, 1, 1) forwards;
}

@keyframes opm-in {
  from {
    opacity: 0;
    transform: scale(0.92) translateY(24px);
    filter: blur(4px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
    filter: blur(0);
  }
}

@keyframes opm-out {
  from {
    opacity: 1;
    transform: scale(1) translateY(0);
    filter: blur(0);
  }
  to {
    opacity: 0;
    transform: scale(0.96) translateY(8px);
    filter: blur(3px);
  }
}

/* ==========================================================================
   RESPONSIVE - MOBILE (< 640px)
   ========================================================================== */

@media (max-width: 640px) {
  .opm-content {
    max-width: none;
    margin: 0 1rem;
    max-height: 90vh;
    border-radius: 1.25rem;
  }

  .opm-hero {
    border-top-left-radius: 1.25rem;
    border-top-right-radius: 1.25rem;
  }

  .opm-header {
    padding: 1.25rem 1.25rem 0.75rem;
  }

  .opm-body {
    padding: 1rem 1.25rem;
  }

  .opm-footer {
    @apply flex-col items-stretch gap-3;
    padding: 0.75rem 1.25rem 1.25rem;
  }

  .opm-tech {
    @apply justify-center;
  }

  .opm-visit-btn {
    @apply justify-center;
  }
}
</style>
